import { Box, Grid, Pagination } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DataTable, { DataTableFilter, DataTableFilterItem } from "../components/DataTable";
import Meal from "../components/Meal";
import Subheader from "../components/Subheader";
import Api from "../openapi";
import { DeliveryFeeResponse } from "../openapi/client";
import { GridPaginationModel, GridCallbackDetails, GridSortModel } from "@mui/x-data-grid";
import { EditIcon } from "../assets/icons";
import MealCrud from "../components/MealCrud";
import { checkIf, debounce, formatCurrency, generateRandom } from "../utils";
import useConfirm from "../hooks/useConfirm";
import { DeleteLabelIcon } from "../components/IconLabel";
import { useDispatch } from "react-redux";
import { setNotification } from "../store/notificationSlice";
import FeeCrud from "../components/FeeCrud";
import useZones from "../hooks/useZones";
import useAreasBrief from "../hooks/useAreaBrief";
import { usePlans } from "../hooks/usePlans";

interface IEditMeal {
  show: boolean;
  fee?: DeliveryFeeResponse;
}

export default function DeliveryFee() {
  const dispatch = useDispatch();
  const zones = useZones();
  const areas = useAreasBrief();
  const planList = usePlans();
  const [fees, setFees] = useState<DeliveryFeeResponse[]>([]);
  const [createShow, setCreateShow] = useState(false);
  const [editShow, setEditShow] = useState<IEditMeal>({ show: false, fee: undefined });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retriggerReq, setRetriggerReq] = useState<number>();
  const [filterModel, setFilterModel] = useState<DataTableFilter>({});
  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete a record from the list, and this action is irreversible. Are you sure you want to proceed?",
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "zone",
      sort: "asc",
    },
  ]);

  const handlePaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPaginationModel(model);
  };
  const handleSortChange = (sortModel: GridSortModel) => {
    sortModel.length && setSortModel(sortModel);
  };

  const create = () => {
    setCreateShow(true);
  };

  const edit = (params: any) => {
    setEditShow({ show: true, fee: params.row });
  };
  const deleteRec = async (params: any) => {
    const answer = await confirmDelete();
    if (!answer) return;

    try {
      const req = await Api.DeliveryFee.deleteDeliveryFee(params.row.id);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Delivery fee has been deleted successfully",
        })
      );
      setRetriggerReq(generateRandom());
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const handleClose = () => {
    setCreateShow(false);
    setEditShow({ show: false, fee: undefined });
  };
  const handleConfirm = () => {
    setRetriggerReq(generateRandom());
  };

  const columns = useMemo(() => {
    return [
      {
        field: "zone",
        minWidth: 80,
        flex: 1,
        headerName: "Zone",
        valueGetter: (params: any) => params.row.zone,
      },
      {
        field: "area",
        minWidth: 80,
        flex: 1,
        headerName: "Area",
        valueGetter: (params: any) => params.row.area,
      },
      {
        field: "plans",
        minWidth: 200,
        flex: 1,
        headerName: "Plan",
        valueGetter: (params: any) => params.row.plans.map((i: any) => i.name).join(","),
      },
      {
        field: "fee",
        minWidth: 100,
        flex: 1,
        headerName: "Fee",
        valueGetter: (params: any) => formatCurrency(params.row.fee),
      },
    ];
  }, [fees]);

  const filterItems: DataTableFilterItem[] = [
    {
      field: "planId",
      label: "Plan",
      type: "select",
      listData: planList,
    },
    {
      field: "zone",
      label: "Zone",
      type: "select",
      listData: zones,
    },
    {
      field: "area",
      label: "Area",
      type: "select",
      listData: areas,
    },
  ];

  useEffect(() => {
    const getFees = async () => {
      setLoading(true);
      try {
        const res = await Api.DeliveryFee.listDeliveryFees(
          filterModel.area,
          filterModel.zone,
          filterModel.planId,
          paginationModel.page,
          paginationModel.pageSize,
          sortModel.map((i) => `${i.field},${i.sort}`)
        );
        setFees(res.data.content as DeliveryFeeResponse[]);
        setTotalRowCount(Number(res.data.totalElements));
      } catch (error) {}
      setLoading(false);
    };
    getFees();
  }, [paginationModel, sortModel, filterModel, retriggerReq]);

  return (
    <Box className="meals">
      <Subheader page="Delivery Fee" />
      <Box p={6} className="meals-container">
        <DataTable
          rows={fees}
          rowCount={totalRowCount}
          columns={columns}
          sortingMode="server"
          sortModel={sortModel}
          paginationMode="server"
          paginationModel={paginationModel}
          paginationChange={handlePaginationChange}
          sortChange={handleSortChange}
          filterItems={filterItems}
          filterModel={filterModel}
          filterModelChange={setFilterModel}
          loading={loading}
          create={create}
          edit={edit}
          deleteRec={deleteRec}
          options={{
            entityName: "delivery Fee",
            permission: { create: "DELIVERY_FEE_CREATE", edit: "DELIVERY_FEE_EDIT", delete: "DELIVERY_FEE_DELETE" },
          }}
        />
      </Box>
      <FeeCrud
        create
        open={createShow}
        title="Create New Delivery Fee"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <FeeCrud
        edit
        open={editShow.show}
        deliveryFee={editShow.fee}
        title="Edit Delivery Fee"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <ConfirmDialog />
    </Box>
  );
}
